import React, { useEffect, useRef, useState } from 'react';
import { ImagePlus, X, Loader2 } from 'lucide-react';
import { apiFetch, ApiError } from '../../../lib/api';
import { IMAGE_TYPES, MAX_IMAGE_BYTES, ALT_MAX, imageDescriptionProblem } from './inlineEdit';
import { absoluteSrc, PictureSwap } from './InlineChangeSummary';

// Replace a picture (docs/inline-edit-lab.md §8, trial feedback C): the current
// picture, one file (JPEG/PNG/WebP ≤ 10 MB), then "Current → New" and a FRESH
// description — the old one describes the old picture. "Use the previous
// description" fills it back in when it still fits. A decorative picture
// (target.decorative) needs none and is sent with alt ''; a picture that is
// the only label of a link or button (target.nameBearing) must say where it
// leads. Upload goes to /api/upload (scope edit-requests), exactly like
// FileUploadField; the preview is the file's data URL.

const labelClasses = 'block text-[10px] font-medium text-gray-500 mb-1.5 tracking-wider uppercase';
const inputClasses =
  'w-full px-3.5 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30 focus:border-[#B0FC38]/30 focus:bg-white/[0.05] transition-all';
const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B0FC38]/60';

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(new Error('Could not read the picture'));
    r.readAsDataURL(file);
  });
}

export default function InlineImageForm({ target, siteOrigin, bridge, onUploadingChange, onClosed }) {
  const generated = useRef(`ie_portal_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`);
  const changeId = target.changeId || target.id || generated.current;
  const decorative = target.decorative === true;
  const nameBearing = target.nameBearing === true;
  const [upload, setUploadState] = useState(() => bridge.uploads.get(changeId) || null);
  const [alt, setAlt] = useState(() => (decorative ? '' : (bridge.uploads.get(changeId)?.alt ?? '')));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const altRef = useRef(null);
  const headingRef = useRef(null);
  const focusAltRef = useRef(false);
  const inputId = `inline-image-file-${changeId}`;
  const altId = `inline-image-alt-${changeId}`;
  const altHelpId = `${altId}-help`;
  const altErrorId = `${altId}-error`;
  const doneHintId = `${inputId}-done-hint`;
  const previousAlt = String(target.alt || '').trim();
  const hasNew = !!upload?.dataUrl;
  const problem = hasNew ? imageDescriptionProblem({ ...upload, alt, decorative, nameBearing }) : null;
  const doneHint = !hasNew ? 'Choose a new picture first.' : problem;

  useEffect(() => { headingRef.current?.focus(); }, []);
  useEffect(() => () => onUploadingChange?.(false), [onUploadingChange]);
  // A new picture was chosen: its description field takes focus.
  useEffect(() => {
    if (focusAltRef.current && altRef.current) { focusAltRef.current = false; altRef.current.focus(); }
  }, [upload]);

  const onFile = async (file) => {
    if (!file) return;
    setError('');
    if (!IMAGE_TYPES.includes(file.type)) { setError('Choose a JPEG, PNG or WebP picture.'); return; }
    if (file.size > MAX_IMAGE_BYTES) { setError('That picture is larger than 10 MB. Choose a smaller one.'); return; }
    setBusy(true);
    onUploadingChange?.(true);
    try {
      const fd = new FormData();
      fd.append('scope', 'edit-requests');
      fd.append('files', file);
      const data = await apiFetch('/api/upload', { method: 'POST', body: fd });
      const att = Array.isArray(data?.attachments) ? data.attachments[0] : null;
      if (!att?.key) throw new Error('Upload failed');
      const dataUrl = await readAsDataUrl(file);
      // A new picture needs its own description: start from an empty field.
      const next = {
        key: att.key,
        filename: att.filename || file.name,
        size: att.size || file.size,
        contentType: att.contentType || file.type,
        alt: '',
        dataUrl,
        decorative,
        nameBearing,
      };
      bridge.setUpload(changeId, next);
      setUploadState(next);
      setAlt('');
      focusAltRef.current = !decorative;
      bridge.previewImage({ changeId, src: dataUrl, alt: '' });
    } catch (err) {
      setError(err instanceof ApiError ? (err.message || 'Upload failed') : 'Upload failed, try again.');
    } finally {
      setBusy(false);
      onUploadingChange?.(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Keep the page preview's alt in step with the field.
  const commitAlt = (value = alt) => {
    const up = bridge.uploads.get(changeId) || upload;
    const clean = decorative ? '' : String(value).trim().slice(0, ALT_MAX);
    if (!up?.dataUrl || up.alt === clean) return;
    bridge.setUpload(changeId, { alt: clean });
    setUploadState((u) => (u ? { ...u, alt: clean } : u));
    bridge.previewImage({ changeId, src: up.dataUrl, alt: clean });
  };

  const cancel = () => {
    if (bridge.changes.has(changeId)) bridge.undo(changeId);
    else if (upload) bridge.setUpload(changeId, null);
    bridge.clearSelected();
  };
  const done = () => {
    if (doneHint) return;
    commitAlt();
    bridge.clearSelected();
    onClosed?.(changeId);
  };
  const usePrevious = () => {
    setAlt(previousAlt);
    commitAlt(previousAlt);
    altRef.current?.focus();
  };

  const currentSrc = target.url || absoluteSrc(target.src, siteOrigin);
  const altMissing = hasNew && !decorative && !String(alt).trim();

  return (
    <section aria-labelledby={`${inputId}-heading`} className="rounded-xl bg-white/[0.02] border border-[#B0FC38]/25 p-3 flex flex-col gap-3" data-testid="inline-image-form">
      <div className="flex items-center gap-2">
        <ImagePlus className="w-3.5 h-3.5 text-[#B0FC38]" aria-hidden="true" />
        <h3 id={`${inputId}-heading`} ref={headingRef} tabIndex={-1} className={`text-[13px] font-medium text-white flex-1 ${focusRing} rounded`}>Replace this picture</h3>
        <button type="button" onClick={cancel} aria-label="Cancel picture change" className={`${focusRing} p-1 rounded text-gray-500 hover:text-white`}>
          <X className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      </div>

      {hasNew ? (
        <div data-testid="inline-image-swap">
          <div className="flex items-center gap-2 text-[10px] text-gray-500 uppercase tracking-wider mb-1" aria-hidden="true">
            <span className="w-24">Current</span><span className="w-3.5" /><span>New</span>
          </div>
          <PictureSwap
            size="w-24 h-16"
            beforeSrc={currentSrc}
            beforeLabel={`Current picture${previousAlt ? `: ${previousAlt}` : ''}`}
            afterSrc={upload.dataUrl}
            afterLabel={`New picture: ${upload.filename || ''}`}
          />
        </div>
      ) : currentSrc ? (
        <img src={currentSrc} alt={`Current picture${previousAlt ? `: ${previousAlt}` : ''}`} className="max-h-28 w-auto self-start rounded border border-white/[0.08]" />
      ) : null}

      <div>
        <label htmlFor={inputId} className={labelClasses}>{hasNew ? 'Choose a different picture' : 'New picture'}</label>
        <input id={inputId} ref={fileRef} type="file" accept={IMAGE_TYPES.join(',')} disabled={busy}
          onChange={(e) => onFile(e.target.files?.[0])}
          className={`${focusRing} block w-full text-[12px] text-gray-300 file:mr-3 file:px-3 file:py-1.5 file:rounded-full file:border-0 file:bg-[#B0FC38]/[0.15] file:text-white hover:file:bg-[#B0FC38]/[0.25]`} />
        <p className="mt-1 text-[11px] text-gray-500">JPEG, PNG or WebP, up to 10 MB. It is fitted to the same size as the current picture.</p>
        {busy && <p className="mt-1 text-[11px] text-gray-400 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> Uploading…</p>}
      </div>

      {decorative ? (
        <p className="text-[12px] text-gray-300" data-testid="inline-image-decorative">This picture is decorative, so it needs no description.</p>
      ) : hasNew ? (
        <div>
          <label htmlFor={altId} className={labelClasses}>Description of the new picture (required)</label>
          {nameBearing && (
            <p id={altHelpId} className="mb-1.5 text-[11px] text-yellow-100/90">This picture is the only label of a link or button — describe where it leads.</p>
          )}
          <input id={altId} ref={altRef} type="text" value={alt} maxLength={ALT_MAX} required aria-required="true"
            aria-invalid={!!problem}
            aria-describedby={[nameBearing ? altHelpId : null, problem ? altErrorId : null].filter(Boolean).join(' ') || undefined}
            onChange={(e) => setAlt(e.target.value)} onBlur={() => commitAlt()}
            placeholder="Describe the new picture for people who can’t see it" className={inputClasses} />
          {previousAlt && alt.trim() !== previousAlt && (
            <button type="button" onClick={usePrevious}
              className={`${focusRing} mt-2 inline-flex items-center px-2.5 py-1 rounded-full border border-white/[0.12] text-[11px] text-gray-300 hover:text-white hover:border-white/[0.25]`}>
              Use the previous description
            </button>
          )}
          {problem && (
            <p id={altErrorId} className="mt-1 text-[11px] text-red-300">{altMissing ? 'Describe the new picture for people who can’t see it.' : problem}</p>
          )}
        </div>
      ) : null}

      {error && <p role="alert" className="text-[11px] text-red-300">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        {!hasNew && <p id={doneHintId} className="text-[11px] text-gray-500 mr-auto">{doneHint}</p>}
        <button type="button" onClick={done} disabled={busy || !!doneHint}
          aria-describedby={!hasNew ? doneHintId : (problem && !decorative ? altErrorId : undefined)}
          className={`${focusRing} px-3.5 py-1.5 rounded-full bg-[#B0FC38]/[0.15] border border-[#B0FC38]/30 text-[12px] text-white hover:bg-[#B0FC38]/[0.25] disabled:opacity-50 disabled:cursor-not-allowed`}>
          Done
        </button>
      </div>
    </section>
  );
}
